// Simplified CustomerReviews.js component
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Button } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios';
import './CustomerReviews.css';

const CustomerReviews = () => {
    // Single default review to use as fallback
    const defaultReview = {
        id: 1,
        name: "Ahmet Yılmaz",
        location: "İstanbul",
        rating: 5,
        text: "Otoprime sayesinde hayalimdeki arabaya kolayca sahip oldum. Tüm süreç çok profesyonel ve şeffaf ilerledi. Aylık ödemelerim bütçeme uygun, kesinlikle tavsiye ediyorum!"
    };

    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Fetch reviews from API or use default
    useEffect(() => {
        const fetchReviews = async () => {
            try {
                setLoading(true);
                const response = await axios.get('/api/reviews', {
                    timeout: 3000 // 3 second timeout
                });

                if (response.data && response.data.reviews && response.data.reviews.length > 0) {
                    setReviews(response.data.reviews);
                } else {
                    // Use default review if no data from API
                    setReviews([defaultReview]);
                }
            } catch (err) {
                console.error('Error fetching reviews:', err);
                // Use default review if API fails
                setReviews([defaultReview]);
            } finally {
                setLoading(false);
            }
        };

        fetchReviews();
    }, []);

    // Navigation functions
    const goPrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        } else {
            // Loop to last page
            setCurrentIndex(Math.ceil(reviews.length / 2) - 1);
        }
    };

    const goNext = () => {
        if (currentIndex < Math.ceil(reviews.length / 2) - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            // Loop to first page
            setCurrentIndex(0);
        }
    };

    // Loading state
    if (loading) {
        return (
            <Container className="py-5 text-center">
                <h2 className="section-title text-center mb-5">Bizi Müşterilerimizden Dinleyin</h2>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </Spinner>
            </Container>
        );
    }

    // Show 2 reviews per page
    const itemsPerPage = 2;
    const startIdx = currentIndex * itemsPerPage;
    const visibleReviews = reviews.slice(startIdx, startIdx + itemsPerPage);
    const totalPages = Math.ceil(reviews.length / itemsPerPage);

    // Page indicator dots
    const renderPageIndicators = () => {
        return (
            <div className="page-indicators">
                {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                        key={index}
                        className={`page-dot ${index === currentIndex ? 'active' : ''}`}
                        onClick={() => setCurrentIndex(index)}
                        aria-label={`Page ${index + 1}`}
                    />
                ))}
            </div>
        );
    };

    // Star rating helper function
    const renderStars = (rating) => {
        return (
            <div className="review-stars">
                {[...Array(5)].map((_, i) => {
                    // Full star
                    if (i < Math.floor(rating)) {
                        return <i key={i} className="fas fa-star"></i>;
                    }
                    // Half star
                    else if (i === Math.floor(rating) && rating % 1 !== 0) {
                        return <i key={i} className="fas fa-star-half-alt"></i>;
                    }
                    // Empty star
                    else {
                        return <i key={i} className="far fa-star"></i>;
                    }
                })}
            </div>
        );
    };

    return (
        <section className="customer-reviews py-5 bg-light">
            <Container>
                <h2 className="section-title text-center mb-5">Bizi Müşterilerimizden Dinleyin</h2>

                <div className="reviews-container">
                    {reviews.length > 2 && (
                        <Button
                            variant="light"
                            className="nav-button prev-button"
                            onClick={goPrevious}
                            aria-label="Önceki yorumlar"
                        >
                            <FaChevronLeft />
                        </Button>
                    )}

                    <div className="reviews-cards-container">
                        <Row className="reviews-row">
                            {visibleReviews.map(review => (
                                <Col md={6} key={review.id || review._id} className="mb-4">
                                    <Card className="review-card h-100">
                                        <Card.Body className="text-center p-4">
                                            <p className="review-text mb-4">{review.text}</p>
                                            <div className="d-flex justify-content-center mb-2">
                                                {renderStars(review.rating)}
                                            </div>
                                            <h5 className="review-name mb-1">{review.name}</h5>
                                            <p className="review-location text-muted small">{review.location}</p>
                                            {review.createdAt && (
                                                <p className="review-date text-muted small">
                                                    {new Date(review.createdAt).toLocaleDateString('tr-TR')}
                                                </p>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>

                    {reviews.length > 2 && (
                        <Button
                            variant="light"
                            className="nav-button next-button"
                            onClick={goNext}
                            aria-label="Sonraki yorumlar"
                        >
                            <FaChevronRight />
                        </Button>
                    )}
                </div>

                {totalPages > 1 && renderPageIndicators()}
            </Container>
        </section>
    );
};

export default CustomerReviews;